/**
 * PortalHeaderUserMenu — shared authenticated user pill + Sign out.
 *
 * Every portal shell (Institution, Admin, Developer, Demo) needs an
 * obvious authenticated logout path. Before 2026-02-21 the header
 * strips exposed only the user email + role pill, with no way for an
 * operator to end the session without navigating to a hidden route.
 *
 * This component keeps the shared visual language of the portal
 * headers (transparent pill on the dark chrome) and adds a compact
 * Sign out affordance that calls `AuthContext.logout()` — which hits
 * `POST /api/auth/logout`, clears the access/refresh cookies, then
 * navigates to `/login`.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function PortalHeaderUserMenu({
  variant = "institution",
  testIdPrefix = "portal",
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const onSignOut = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await logout();
    } finally {
      // Always land the operator back on /login, even if the server
      // logout call fails — the local session was cleared by the
      // context regardless.
      navigate("/login");
    }
  };

  // Colour variants match the per-portal accents already established
  // on the portal shells (institution=cyan default, admin=amber,
  // developer=violet, demo=amber-warn).
  const pillClass =
    variant === "admin"
      ? "vlk-portal-role-pill vlk-portal-role-pill--internal"
      : variant === "developer"
      ? "vlk-portal-role-pill vlk-portal-role-pill--developer"
      : "vlk-portal-role-pill";

  return (
    <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-user-menu`}>
      <span data-testid={`${testIdPrefix}-user-email`}>{user?.email}</span>
      <span className={pillClass} data-testid={`${testIdPrefix}-role-badge`}>
        {user?.role}
      </span>
      <button
        type="button"
        onClick={onSignOut}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded-sm border border-white/20 bg-white/[0.04] px-2 py-1 text-[11px] uppercase tracking-widest text-white/80 hover:bg-white/[0.10] hover:text-white transition-colors disabled:opacity-50"
        title="Sign out"
        aria-label="Sign out"
        data-testid={`${testIdPrefix}-signout`}
      >
        <LogOut className="h-3 w-3" />
        <span className="hidden sm:inline">{busy ? "Signing out…" : "Sign out"}</span>
      </button>
    </div>
  );
}
